import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

// Team uses a self-contained client list fetch
function useTeamClientNames(){
  const [names, setNames] = useState([]);
  useEffect(()=>{
    const q = query(collection(db,"clients"), orderBy("name"));
    const unsub = onSnapshot(q, snap=>{
      if(!snap.empty) setNames(snap.docs.map(d=>d.data().name).sort());
    });
    return ()=>unsub();
  },[]);
  return names;
}
const ROLES = ["Bookkeeper","Tax Preparer","Admin","Staff"];

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"16px 20px",marginBottom:16},
  btn:{fontSize:12,padding:"5px 13px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 13px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnSm:{fontSize:11,padding:"3px 9px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"},
  btnDanger:{fontSize:11,padding:"3px 9px",borderRadius:6,border:"0.5px solid #FCEBEB",background:"#FCEBEB",color:"#A32D2D",cursor:"pointer"},
  input:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 8px",borderRadius:20,background:bg,color:c,fontWeight:500}),
  label:{fontSize:11,color:"#888",marginBottom:3,display:"block"},
};

const ROLE_COLORS = {
  Admin:["#EEEDFE","#3C3489"],Bookkeeper:["#E6F1FB","#185FA5"],
  "Tax Preparer":["#E1F5EE","#085041"],Staff:["#f5f5f0","#555"],
};

const BLANK = {name:"",email:"",role:"Bookkeeper",assignedClients:[],status:"Active",notes:""};

function Avatar({name,size=36}){
  const words=(name||"?").trim().split(" ");
  const initials=((words[0]?.[0]||"")+(words[1]?.[0]||"")).toUpperCase();
  const colors=[["#fdf0f0","#C0111F"],["#E6F1FB","#185FA5"],["#E1F5EE","#0F6E56"],["#EEEDFE","#534AB7"]];
  const [bg,c]=colors[(name||"").length%colors.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:bg,color:c,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.36,fontWeight:500,flexShrink:0}}>{initials}</div>;
}

function ClientMultiSelect({selected,onChange}){
  const CLIENT_NAMES = useTeamClientNames();
  const [search,setSearch]=useState("");
  const filtered=CLIENT_NAMES.filter(c=>c.toLowerCase().includes(search.toLowerCase()));
  const toggle=(name)=>onChange(selected.includes(name)?selected.filter(x=>x!==name):[...selected,name]);
  return (
    <div style={{border:"0.5px solid #ccc",borderRadius:8,overflow:"hidden"}}>
      <div style={{padding:"8px 10px",borderBottom:"0.5px solid #e0dfd8",display:"flex",gap:8,alignItems:"center",background:"#f5f5f0"}}>
        <input style={{...S.input,flex:1}} placeholder="Search clients..." value={search} onChange={e=>setSearch(e.target.value)}/>
        <button style={{...S.btnSm,whiteSpace:"nowrap"}} onClick={()=>onChange(CLIENT_NAMES)}>All</button>
        <button style={{...S.btnSm,whiteSpace:"nowrap"}} onClick={()=>onChange([])}>None</button>
      </div>
      <div style={{maxHeight:220,overflowY:"auto"}}>
        {filtered.map(c=>(
          <div key={c} onClick={()=>toggle(c)} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",cursor:"pointer",background:selected.includes(c)?"#fdf0f0":"#fff",borderBottom:"0.5px solid #f5f5f0"}}>
            <div style={{width:14,height:14,borderRadius:3,border:"0.5px solid #ccc",background:selected.includes(c)?"#C0111F":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {selected.includes(c)&&<span style={{color:"#fff",fontSize:9,lineHeight:1}}>✓</span>}
            </div>
            <span style={{fontSize:12,color:selected.includes(c)?"#C0111F":"#1a1a18",fontWeight:selected.includes(c)?500:400}}>{c}</span>
          </div>
        ))}
      </div>
      <div style={{padding:"6px 12px",borderTop:"0.5px solid #e0dfd8",fontSize:11,color:"#888",background:"#f5f5f0"}}>
        {selected.length} of {CLIENT_NAMES.length} clients selected
      </div>
    </div>
  );
}

function MemberModal({member,onClose,onSave}){
  const [form,setForm]=useState({...BLANK,...member});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const isEdit=!!member.firestoreId;
  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:"#fff",borderRadius:16,width:600,maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",borderBottom:"0.5px solid #e0dfd8",flexShrink:0}}>
          <div style={{fontSize:14,fontWeight:500}}>{isEdit?"Edit Team Member":"Add Team Member"}</div>
          <button style={{...S.btn,padding:"3px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div><label style={S.label}>Full name</label><input style={S.input} value={form.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. John Smith"/></div>
            <div><label style={S.label}>Email address</label><input style={S.input} type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="john@email.com"/></div>
            <div><label style={S.label}>Role</label>
              <select style={S.input} value={form.role} onChange={e=>set("role",e.target.value)}>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status} onChange={e=>set("status",e.target.value)}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <label style={S.label}>Notes</label>
            <input style={S.input} value={form.notes} onChange={e=>set("notes",e.target.value)} placeholder="Any notes about this team member..."/>
          </div>
          <div>
            <label style={{...S.label,marginBottom:8}}>
              Assigned Clients <span style={{fontWeight:400,color:"#aaa",marginLeft:6}}>— this member only sees these clients when logged in</span>
            </label>
            <ClientMultiSelect selected={form.assignedClients} onChange={v=>set("assignedClients",v)}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",padding:"14px 20px",borderTop:"0.5px solid #e0dfd8",flexShrink:0}}>
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={S.btnPrimary} onClick={()=>{if(!form.name||!form.email){alert("Name and email are required.");return;}onSave(form);}}>
            {isEdit?"Save Changes":"Add Team Member"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Team(){
  const [members,setMembers]=useState([]);
  const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null);
  const [expandedId,setExpandedId]=useState(null);

  useEffect(()=>{
    const q=query(collection(db,"team"),orderBy("name"));
    const unsub=onSnapshot(q,snap=>{
      setMembers(snap.docs.map(d=>({...d.data(),firestoreId:d.id})));
      setLoading(false);
    },err=>{ console.error(err); setLoading(false); });
    return ()=>unsub();
  },[]);

  const saveMember=async(form)=>{
    const {firestoreId,...data}=form;
    try{
      if(firestoreId){
        await updateDoc(doc(db,"team",firestoreId),data);
        setJustSaved(null);
      } else {
        const ref = await addDoc(collection(db,"team"),data);
        setJustSaved(ref.id);
      }
      setModal(null);
    }catch(e){ alert("Save failed: "+e.message); }
  };

  const deleteMember=async(firestoreId)=>{
    if(!window.confirm("Remove this team member?")) return;
    try{ await deleteDoc(doc(db,"team",firestoreId)); }catch(e){ alert("Delete failed."); }
  };

  const [inviteStatus, setInviteStatus] = useState({});
  const [justSaved, setJustSaved] = useState(null);

  const sendInvite = async (member) => {
    if(!member.email){ alert("No email address on file for this team member."); return; }
    setInviteStatus(s=>({...s,[member.firestoreId]:"sending"}));
    try {
      const functions = getFunctions();
      const inviteTeamMember = httpsCallable(functions, "inviteClient");
      await inviteTeamMember({
        email: member.email,
        name: member.name,
        firestoreId: null,
      });
      await updateDoc(doc(db,"team",member.firestoreId),{
        inviteSent: true,
        inviteSentAt: new Date().toISOString(),
      });
      setInviteStatus(s=>({...s,[member.firestoreId]:"sent"}));
      alert("✓ Invite sent to "+member.email+"!");
    } catch(e){
      alert("Failed to send invite: "+e.message);
      setInviteStatus(s=>({...s,[member.firestoreId]:"error"}));
    }
  };

  const toggleStatus=async(m)=>{
    const newStatus=m.status==="Active"?"Inactive":"Active";
    try{ await updateDoc(doc(db,"team",m.firestoreId),{status:newStatus}); }catch(e){}
  };

  if(loading) return <div style={{textAlign:"center",padding:"40px 0",color:"#888",fontSize:13}}>Loading team...</div>;

  return (
    <div>
      {modal&&<MemberModal member={modal} onClose={()=>setModal(null)} onSave={saveMember}/>}
      {justSaved&&members.find(m=>m.firestoreId===justSaved)&&(
        <div style={{background:"#EAF3DE",border:"0.5px solid #B5D8A4",borderRadius:12,padding:"14px 18px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:13,fontWeight:500,color:"#3B6D11"}}>✓ Team member added successfully!</div>
            <div style={{fontSize:12,color:"#555",marginTop:2}}>
              Next: Click their card to expand it, then click <strong>Send Portal Invite</strong> to send {members.find(m=>m.firestoreId===justSaved)?.name} their login setup email.
              {" "}If they don't have a Firebase account yet, the portal will walk you through creating one first (30 seconds).
            </div>
          </div>
          <button style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"0.5px solid #B5D8A4",background:"#fff",cursor:"pointer",color:"#3B6D11",flexShrink:0,marginLeft:12}} onClick={()=>setJustSaved(null)}>✕</button>
        </div>
      )}}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500}}>Team Members</div>
        <button style={S.btnPrimary} onClick={()=>setModal({...BLANK})}>+ Add Team Member</button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
        {[["Team Members",members.length,"#185FA5"],["Active",members.filter(m=>m.status==="Active").length,"#3B6D11"],["Inactive",members.filter(m=>m.status==="Inactive").length,"#888"]].map(([l,v,c])=>(
          <div key={l} style={{background:"#f5f5f0",borderRadius:8,padding:"14px 16px"}}>
            <div style={{fontSize:12,color:"#888",marginBottom:6}}>{l}</div>
            <div style={{fontSize:22,fontWeight:500,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {members.length===0?(
        <div style={S.card}>
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <div style={{fontSize:32,marginBottom:12}}>👥</div>
            <div style={{fontSize:14,fontWeight:500,marginBottom:8}}>No team members yet</div>
            <div style={{fontSize:13,color:"#888",marginBottom:20}}>Add your bookkeeper or other staff. Assign them specific clients so they only see their work when they log in.</div>
            <button style={S.btnPrimary} onClick={()=>setModal({...BLANK})}>+ Add Your First Team Member</button>
          </div>
        </div>
      ):(
        <div style={S.card}>
          {members.map((m,idx)=>(
            <React.Fragment key={m.firestoreId}>
              <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px 0",borderBottom:idx<members.length-1||expandedId===m.firestoreId?"0.5px solid #f0efe8":"none",cursor:"pointer"}} onClick={()=>setExpandedId(expandedId===m.firestoreId?null:m.firestoreId)}>
                <Avatar name={m.name}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                    <div style={{fontSize:13,fontWeight:500}}>{m.name}</div>
                    <span style={S.badge(...(ROLE_COLORS[m.role]||["#f5f5f0","#555"]))}>{m.role}</span>
                    <span style={S.badge(m.status==="Active"?"#EAF3DE":"#f5f5f0",m.status==="Active"?"#3B6D11":"#888")}>{m.status}</span>
                  </div>
                  <div style={{fontSize:11,color:"#888"}}>{m.email}</div>
                  <div style={{fontSize:11,color:"#aaa",marginTop:2}}>{(m.assignedClients||[]).length} client{(m.assignedClients||[]).length!==1?"s":""} assigned</div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button style={S.btnSm} onClick={e=>{e.stopPropagation();setModal(m);}}>Edit</button>
                  <button style={{...S.btnSm,color:m.status==="Active"?"#854F0B":"#3B6D11"}} onClick={e=>{e.stopPropagation();toggleStatus(m);}}>{m.status==="Active"?"Deactivate":"Activate"}</button>
                  <button style={S.btnDanger} onClick={e=>{e.stopPropagation();deleteMember(m.firestoreId);}}>Remove</button>
                </div>
              </div>
              {expandedId===m.firestoreId&&(
                <div style={{padding:"12px 0 16px",borderBottom:"0.5px solid #f0efe8"}}>
                  {m.notes&&<div style={{fontSize:12,color:"#888",marginBottom:10,fontStyle:"italic"}}>"{m.notes}"</div>}
                  <div style={{fontSize:11,fontWeight:500,color:"#888",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>Assigned Clients ({(m.assignedClients||[]).length})</div>
                  {(m.assignedClients||[]).length===0
                    ?<div style={{fontSize:12,color:"#aaa"}}>No clients assigned. Click Edit to assign clients.</div>
                    :<div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {m.assignedClients.map(c=>(
                        <span key={c} style={{fontSize:11,padding:"3px 9px",borderRadius:20,background:"#E6F1FB",color:"#185FA5",border:"0.5px solid #B5D4F4"}}>{c}</span>
                      ))}
                    </div>
                  }
                  <div style={{marginTop:12,paddingTop:10,borderTop:"0.5px solid #f0efe8",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <button
                      style={{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",
                        background:inviteStatus[m.firestoreId]==="sent"||m.inviteSent?"#EAF3DE":"#C0111F",
                        color:"#fff",cursor:inviteStatus[m.firestoreId]==="sending"?"wait":"pointer",
                        fontFamily:"sans-serif"
                      }}
                      onClick={()=>sendInvite(m)}
                      disabled={inviteStatus[m.firestoreId]==="sending"}
                    >
                      {inviteStatus[m.firestoreId]==="sending"?"Sending..."
                        :inviteStatus[m.firestoreId]==="sent"||m.inviteSent?"✓ Invite Sent — Resend"
                        :"✉ Send Portal Invite"}
                    </button>
                    {m.inviteSentAt&&<span style={{fontSize:11,color:"#aaa"}}>Last sent {new Date(m.inviteSentAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</span>}
                    {(inviteStatus[m.firestoreId]==="no-account"||(!m.inviteSent&&!inviteStatus[m.firestoreId]))&&(
                      <span style={{fontSize:11,color:"#888"}}>
                        Step 1: <a href="https://console.firebase.google.com/project/ficsori-portal/authentication/users" target="_blank" rel="noreferrer" style={{color:"#185FA5"}}>Add Firebase account</a> → Step 2: Click Send Portal Invite
                      </span>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={{...S.card,background:"#f5f5f0",border:"none"}}>
        <div style={{fontSize:12,fontWeight:500,marginBottom:8}}>How team access works</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
          {[["1. Add team member","Enter their name, email, role, and assign specific clients.","#185FA5"],
            ["2. Create their login","Add their email in Firebase Console → Authentication → Add user.","#3B6D11"],
            ["3. They see only their clients","The portal filters CRM, Files, Tasks, and Time Tracking to their assigned clients automatically.","#854F0B"]
          ].map(([t,d,c])=>(
            <div key={t}>
              <div style={{fontSize:12,fontWeight:500,color:c,marginBottom:4}}>{t}</div>
              <div style={{fontSize:11,color:"#888",lineHeight:1.6}}>{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
